import { useEffect, useRef, useState } from 'react';
import { useOtpMutation } from '../../../../store/api-slices';
import useAnalytics from '../../../../hooks/analytics/useAnalytics';
import {
  TURNSTILE_ENABLED,
  TURNSTILE_TYPE_HEADER,
  TURNSTILE_VERIFICATION_TOKEN_HEADER,
} from '../../../../components-library/auth/OtpFlow';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import collect from 'collect.js';
import { ITurnstileVerifyPayload, TurnstileType } from '../../../../components-library/auth/turnstile/types';
import { delay } from '../../../../utils/loading';
import { OTP_DELIVERY_CHANNELS, OTP_REQUEST_ERROR_CODE } from '../../../../services/exports/Constants';
import { LoginSchema } from './schema';
import useOtpChannels from '../../../../hooks/auth/useOtpChannels';

// How long before a resend is allowed (and the captcha reappears for a fresh token).
// Mirrors the web OtpFlow fallback used when the response omits a `retry-after` header.
const RESEND_LOCKOUT_SECONDS = 30;

const useOtpFlow = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Modals:Login:useOtpFlow' });
  const { defaultChannel } = useOtpChannels();
  const [requestOtp, otpResult] = useOtpMutation();
  const { trackOtpRequested, trackOtpResendRequested } = useAnalytics();
  const [loading, setLoading] = useState<boolean>(false);
  const [lastSentChannel, setLastSentChannel] = useState<OTP_DELIVERY_CHANNELS>(null);
  const [retryAfter, setRetryAfter] = useState<number>(0);
  const tokenRef = useRef<string>('');
  const typeRef = useRef<TurnstileType>(TurnstileType.Cloudflare);
  const resendCount = useRef(0);

  // Tick the resend lockout down to zero. Keyed on the boolean so the interval is set up
  // once per lockout (not re-created every second).
  const isLocked = retryAfter > 0;
  useEffect(() => {
    if (!isLocked) {
      return;
    }

    const timer = setInterval(() => setRetryAfter((current) => (current <= 1 ? 0 : current - 1)), 1000);

    return () => clearInterval(timer);
  }, [isLocked]);

  // Each token is single-use, so a successful request consumes it and starts the lockout
  // window; the captcha reappears once it elapses to solve a fresh one for the next resend.
  const onRequestSuccess = (channel: OTP_DELIVERY_CHANNELS) => {
    tokenRef.current = '';
    setLastSentChannel(channel);
    setRetryAfter(RESEND_LOCKOUT_SECONDS);
  };

  const send = async (args: LoginSchema & { delivery_channel?: OTP_DELIVERY_CHANNELS; email?: string }) => {
    if (!tokenRef.current && TURNSTILE_ENABLED) {
      toast.error(t('feedback.token_required'));

      return Promise.reject(t('feedback.token_required'));
    }

    setLoading(true);
    await delay();
    const channel = args.delivery_channel ?? defaultChannel;
    // company_id/phone_number are guaranteed by the validated login form; the zod-inferred
    // LoginSchema types them as optional (strictNullChecks is off), so cast to the API contract.
    const request = await requestOtp({
      ...args,
      headers: { [TURNSTILE_VERIFICATION_TOKEN_HEADER]: tokenRef.current, [TURNSTILE_TYPE_HEADER]: typeRef.current },
    } as Parameters<typeof requestOtp>[0]);
    setLoading(false);

    if (!(request as any)?.error) {
      onRequestSuccess(channel);
      trackOtpRequested({ otp_channel: channel, channel_is_default: channel === defaultChannel });
    }

    switch ((request as any)?.error?.status) {
      case 422: {
        const body = (request as any).error.data.response.data;

        // Existing account with no email on file: the backend's description already names
        // the provider's support address, and there is no field the customer can fix.
        if (body?.error_code === OTP_REQUEST_ERROR_CODE.EMAIL_NOT_ON_FILE) {
          toast.error(body.description);
          break;
        }

        collect(body?.errors)
          .values()
          .each((error: string) => toast.error(error));
        break;
      }

      case 429:
        toast.error(t('feedback.frequent_requests'));
        break;
    }
  };

  const resend = async (channel?: OTP_DELIVERY_CHANNELS) => {
    if (loading || retryAfter > 0) {
      return;
    }

    // The captcha is shown once the lockout elapses; require a freshly-solved token,
    // otherwise the backend rejects the already-consumed one.
    if (!tokenRef.current && TURNSTILE_ENABLED) {
      toast.error(t('feedback.token_required'));

      return;
    }

    setLoading(true);
    await delay();
    const args = {
      ...otpResult.originalArgs!,
      ...(channel ? { delivery_channel: channel } : {}),
      headers: { [TURNSTILE_VERIFICATION_TOKEN_HEADER]: tokenRef.current, [TURNSTILE_TYPE_HEADER]: typeRef.current },
    };
    const request = await requestOtp(args);
    setLoading(false);

    if (!(request as any)?.error) {
      const sentChannel = args.delivery_channel ?? lastSentChannel;
      resendCount.current += 1;
      trackOtpResendRequested({
        otp_channel: sentChannel,
        switched_on_resend: Boolean(channel) && channel !== lastSentChannel,
        resend_number: resendCount.current,
      });
      onRequestSuccess(sentChannel);
    }
  };

  return {
    otpResult,
    loading,
    send,
    resend,
    lastSentChannel,
    retryAfter,
    attachToken: (payload: ITurnstileVerifyPayload) => {
      tokenRef.current = payload.token;
      typeRef.current = payload.type;
    },
    hasTurnstile: TURNSTILE_ENABLED,
  };
};

export default useOtpFlow;
